import { z } from 'zod';

/**
 * Shelf-label bookkeeping: which product barcodes have a label on the shelf, and
 * at what price.
 *
 * A "label source" is one printable barcode of a product. It is the same set the
 * label designer offers:
 *   - `primary`: the product's own barcode;
 *   - `unit:<id>`: a pack/carton unit with its own barcode;
 *   - `variant:<id>`: a variant with its own barcode;
 *   - `code`: a product with no barcode at all, labelled by its code.
 * It "needs a label" when nothing was ever printed for it (`new`), or when what
 * was printed no longer matches the product (`price` / `barcode`). That is
 * computed by comparing against the CURRENT product, never stored as a flag, so a
 * price changed anywhere — the item form, an Excel import, the manager app, a
 * script — puts the label back on the list with nobody having to remember to.
 */
export const LabelSourceKeySchema = z
  .string()
  .regex(/^(primary|code|unit:[0-9a-f-]{36}|variant:[0-9a-f-]{36})$/i);
export type LabelSourceKey = z.infer<typeof LabelSourceKeySchema>;

export const LabelPendingReasonSchema = z.enum(['new', 'price', 'barcode']);
export type LabelPendingReason = z.infer<typeof LabelPendingReasonSchema>;

/** One barcode of one product whose shelf label is missing or out of date. */
export const PendingLabelSchema = z.object({
  key: LabelSourceKeySchema,
  reason: LabelPendingReasonSchema,
  barcode: z.string().nullable(),
  price: z.string().nullable(),
  currencyCode: z.string().nullable(),
  /** What the shelf label says now — null when none was ever printed. */
  printedBarcode: z.string().nullable(),
  printedPrice: z.string().nullable(),
  printedCurrencyCode: z.string().nullable(),
  printedAt: z.string().nullable(),
});
export type PendingLabel = z.infer<typeof PendingLabelSchema>;

/** `GET /items/labels/pending` filters. */
export const LabelPendingFilter = z.enum(['all', 'new', 'changed']);
export type LabelPendingFilter = z.infer<typeof LabelPendingFilter>;

/** "This label is on the shelf now": the barcode and price that went on paper. */
export const LabelMarkSchema = z.object({
  itemId: z.string().uuid(),
  key: LabelSourceKeySchema,
  barcode: z.string().max(64).nullable(),
  // A plain decimal, as the item DTO sends it ("1500.0000") — it lands in DECIMAL(19,4).
  price: z.string().regex(/^-?\d{1,15}(\.\d{1,8})?$/).nullable(),
  currencyCode: z.string().max(3).nullable(),
});
export type LabelMark = z.infer<typeof LabelMarkSchema>;

/**
 * `POST /items/labels/marks`.
 *
 * `printed` records labels as printed with the values the CALLER printed — not
 * the product's values at the time the request lands. The manager app marks
 * offline and pushes later; if the price moved in between, the label really does
 * show the old price and must come back as `price`.
 *
 * `unprinted` forgets a label entirely, so it reads `new` again.
 */
export const LabelMarksSchema = z.object({
  printed: z.array(LabelMarkSchema).max(5000).default([]),
  unprinted: z.array(z.object({ itemId: z.string().uuid(), key: LabelSourceKeySchema })).max(5000).default([]),
});
export type LabelMarks = z.infer<typeof LabelMarksSchema>;

/** `POST /items/labels/mark-all`: everything the filter matches, at today's values. */
export const LabelMarkAllSchema = z.object({
  q: z.string().max(200).optional(),
  categoryId: z.string().uuid().optional(),
  brandId: z.string().uuid().optional(),
  filter: LabelPendingFilter.default('all'),
});
export type LabelMarkAll = z.infer<typeof LabelMarkAllSchema>;

/** One row of `GET /items/labels/prints` — what the manager app keeps offline. */
export const LabelPrintSchema = LabelMarkSchema.extend({
  printedAt: z.string(),
});
export type LabelPrint = z.infer<typeof LabelPrintSchema>;
